import {
  Button,
  DatePicker,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';
import { getLocalTimeZone, today } from '@internationalized/date';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/redux/hooks';
import useHospitalFlags from '@/hooks/useHospitalFlags';
import { useUpdatePatientMutation } from '@/patient-app-common/api/patientApi';
import usePatientDates from '@/patient-app-common/hooks/usePatientDates';
import { setDischargePrompt } from '@/patient-app-common/slices/appSlice';

interface SetDischargeProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SetDischarge({ isOpen, onClose }: SetDischargeProps) {
  const { t } = useTranslation('home');
  const { hospital } = useHospitalFlags();
  const dispatch = useAppDispatch();

  const [updatePatient, { isLoading }] = useUpdatePatientMutation();
  const { surgeryDate } = usePatientDates();

  const [page, setPage] = useState(1);

  const [date, setDate] = useState<null | string>(null);
  const [error, setError] = useState('');

  const validateDate = () => {
    let valid = true;

    if (!dayjs(date).isValid()) {
      setError(t('common.error.blank_input'));
      return false;
    }

    if (dayjs(date).isBefore(dayjs(surgeryDate).startOf('day'))) {
      valid = false;
      const surgeryStr = dayjs(surgeryDate).format('LL');
      setError(t('setdischarge.error.invalid_1', { surgeryStr }));
    } else if (dayjs(date).isAfter(dayjs().endOf('day'))) {
      valid = false;
      setError(t('setdischarge.error.invalid_2'));
    }

    return valid;
  };

  const handleSubmit = async () => {
    const valid = validateDate();
    if (!valid) return;

    const params = {
      confirmedSurgery: true,
      dischargeDate: dayjs(date).locale('en').format('MM/DD/YYYY'),
    };

    const result = await updatePatient(params).unwrap();
    dispatch(setDischargePrompt(false));

    if (result.success) {
      setError('');
      onClose();
    } else {
      setError(t('common.error.server'));
    }
  };

  const handleClose = () => {
    dispatch(setDischargePrompt(false));
    onClose();
    setPage(1);
  };

  return (
    <Modal
      isOpen={isOpen}
      isDismissable={false}
      hideCloseButton
      onClose={onClose}
      size="2xl"
    >
      <ModalContent>
        {page === 1 && (
          <>
            <ModalHeader>{t('setdischarge.title')}</ModalHeader>
            <ModalBody>
              <p>{t('setdischarge.input.hint')}</p>
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                isLoading={isLoading}
                onPress={() => setPage(2)}
              >
                {t('common.action.yes')}
              </Button>
              <Button color="primary" variant="light" onPress={handleClose}>
                {t('common.action.no')}
              </Button>
            </ModalFooter>
          </>
        )}

        {page === 2 && (
          <>
            <ModalHeader>
              {t('setdischarge.input.label', {
                hospitalName: hospital?.displayName || '',
              })}
            </ModalHeader>
            <ModalBody>
              <DatePicker
                label={t('common.input.date')}
                errorMessage={error}
                isInvalid={!_.isEmpty(error)}
                minValue={today(getLocalTimeZone()).subtract({ days: 60 })}
                onChange={(date) => setDate(_.toString(date))}
              />
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                isLoading={isLoading}
                onPress={handleSubmit}
              >
                {t('common.action.save')}
              </Button>
              <Button color="danger" variant="light" onPress={handleClose}>
                {t('common.action.cancel')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
